import { JSX, RefObject } from 'preact';
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import {
  Floating,
  Offset,
  Placement,
  Coords,
  FlipOptions,
  ShiftOptions,
  PositionData
} from '../UNSAFE_Floating';
import { focusOn, focusWithin, getActiveElement } from '../utils/PRIVATE_tabbableUtils';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { Layer } from '../UNSAFE_Layer';
import { Modal } from '../UNSAFE_Modal';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { usePopupAnimation } from './usePopupAnimation';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { popupVars } from './themes/PopupContract.css';
import { getVarName } from '../utils/UNSAFE_stringUtils';
import { Property } from 'csstype';
import { classNames } from '../utils/UNSAFE_classNames';
import { PopupRedwoodTheme } from './themes/redwood/PopupTheme';

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;

type Props = IntrinsicProps & {
  /**
   * Specifies whether the Popup is open.
   */
  isOpen: boolean;
  /**
   * Specifies Popup's anchor. Popup is placed relatively to its anchor.
   */
  anchorRef: RefObject<HTMLElement | Coords>;
  /**
   * Specifies placement of the Popup relative to the anchor.
   */
  placement?: Placement;
  /**
   * Specifies displacement of the Popup from the anchor element placement along the specified axes.
   * The offset object consists of mainAxis and crossAxis properties. The direction in which these properties
   * are applied depends on the current value of the position property.
   *
   * The <code>mainAxis</code> property represents the distance between the Popup and the anchor.
   * The <code>crossAxis</code> property represents the deviation in the opposite axis to the main axis - the skidding between the Popup and the anchor.
   */
  offset?: Offset;
  /**
   * Specifies modality of the Popup.
   */
  modality?: 'modal' | 'modeless';
  /**
   * Specifies whether a floating Popup changes placement to the opposite side to be kept in view when a collision is detected.
   *
   * The flipOptions object consists of mainAxis and crossAxis properties.
   * The <code>mainAxis</code> runs along the side of the Popup element.
   * The <code>crossAxis</code> runs along the alignment of the Popup element.
   */
  flipOptions?: FlipOptions;
  /**
   * Specifies whether a floating Popup shifts along axis to be kept in view when a collision is detected.
   * Note that if both 'flip' and 'shift' are enabled on respective axis, the flip will take precedence.
   *
   * The shiftOptions object consists of mainAxis and crossAxis properties.
   * The <code>mainAxis</code> runs along the side of the Popup element.
   * The <code>crossAxis</code> runs along the alignment of the Popup element.
   */
  shiftOptions?: ShiftOptions;
  /**
   * Specifies if the Popup sets focus to its content when initially open.
   * A value of none prevents the popup from setting focus when open.
   *
   * <code>auto</code> in modeless mode resolves to none.
   * <code>none</code> prevents the popup from setting focus when open.
   * <code>popup</code> sets focus to the root popup container (good choice for touch platforms).
   * <code>firstFocusable</code> defines that a popup should set focus to the first focusable element within the popup's content.
   */
  initialFocus?: 'auto' | 'none' | 'popup' | 'firstFocusable';
  /**
   * Specifies callback triggered when a user clicks outside Popup.
   */
  onClickOutside?: (event: MouseEvent) => void;
  /**
   * Specifies callback triggered when a user tries to close a Popup through UI interaction.
   * The parent should listen to this event and close the Popup. If the parent fails to remove
   * the Popup, then no change will be done in the UI by the component.
   */
  onClose?: (event: KeyboardEvent) => void;
  /**
   * Specifies Popup's tail. Simple tail is an arrow pointing to Popup's anchor.
   */
  tail?: 'none' | 'simple';
  /**
   * Specifies callback triggered after the animation ends.
   */
  onTransitionEnd?: (value: boolean) => void;
  /**
   * Specifies the ARIA role type. Depending on how the popup is used in the page, the page developer should choose from the following:
   * <code>tooltip</code> defines contextual popup that displays a description for an element. This is added automatically if not already specified.
   * <code>dialog</code> defines an application window that is designed to interrupt the current processing of an application in order to prompt the user to enter information or require a response.
   * <code>alertdialog</code> defines type of dialog that contains an alert message, where initial focus goes to an element within the dialog.
   */
  role?: HTMLAttributesSignalExcluded['role'];
} & TestIdProps;

/**
 * A popup temporarily 'pops up' content in the foreground.
 */
export const Popup = forwardRef(
  (
    {
      anchorRef,
      children,
      isOpen = false,
      modality = 'modeless',
      offset = { mainAxis: 0, crossAxis: 0 },
      flipOptions = { mainAxis: true, crossAxis: true },
      shiftOptions = { mainAxis: false, crossAxis: false },
      onClose,
      initialFocus = 'auto',
      onClickOutside,
      placement = 'top',
      tail = 'none',
      onTransitionEnd,
      role = 'tooltip',
      testId
    }: Props,
    ref: ForwardedRef<HTMLElement | null>
  ) => {
    const [trapDisabled, setTrapDisabled] = useState<boolean>(true);
    const launcherRef = useRef<HTMLElement | null>(null);
    const [popupBgColor, setPopupBgColor] = useState<Property.BackgroundColor>();

    const contentWrapperRef = useRef<HTMLDivElement | null>(null);

    // testId support
    const testIdProps = useTestId(testId);

    //The animationPlacement is used for the case of flip.
    const [animationPlacement, setAnimationPlacement] = useState<Placement>(placement);

    function launcherKeyDownCallback(event: KeyboardEvent): void {
      if (launcherRef.current === getActiveElement() && event.code === 'F6') {
        // Prevent default F6 handlers.
        // F6 is a standard Chrome address bar shortcut on Windows.
        event.preventDefault();
        event.stopPropagation();

        focusWithin(contentWrapperRef.current as HTMLElement);
      }
    }

    const _onTransitionEnd = (isOpen: boolean) => {
      if (isOpen) {
        setInitialFocus();
        onTransitionEnd?.(true);
      } else {
        onTransitionEnd?.(false);
      }
    };

    // Animation
    const { setAnimationElementRef, status } = usePopupAnimation({
      isOpen,
      isAnimatedOnMount: true,
      onTransitionEnd: _onTransitionEnd,
      placement: animationPlacement
    });
    const stableRef = useMemo(
      () => mergeRefs(setAnimationElementRef, ref),
      [setAnimationElementRef, ref]
    );

    const returnFocus = useCallback(() => {
      // Try to return focus to 1.launcher or 2.anchor
      const launcherEl = launcherRef.current;
      const anchorEl = anchorRef.current;

      if (launcherEl) {
        focusOn(launcherEl);
        // Checking instanceof to avoid Coords type that can not be focused
      } else if (anchorEl && anchorEl instanceof Element) {
        focusOn(anchorEl);
      }
    }, [anchorRef]);

    useEffect(() => {
      // Init closing
      if (!isOpen && status === 'opening') {
        // Only actively return focus to the launcher or anchor if the popup contained focus.
        // Otherwise, focus is already elsewhere on the page and should not be forced back
        // because that could reopen the popup again, which essentially prevents the user from
        // moving focus out of the launcher.
        if (contentWrapperRef.current?.contains(getActiveElement())) {
          returnFocus();
        }
      }
    }, [isOpen, status, returnFocus]);

    // Launcher handler
    useEffect(() => {
      if (isOpen && anchorRef.current instanceof Element) {
        // Remember launcher
        if (getActiveElement() === document.body) {
          launcherRef.current = anchorRef.current as HTMLElement;
        } else {
          launcherRef.current = getActiveElement() as HTMLElement;
        }

        // Register F6 key handler to enter the Popup
        launcherRef.current?.addEventListener('keydown', launcherKeyDownCallback);
      }
      return () => {
        // Deregister F6 key handler
        launcherRef.current?.removeEventListener('keydown', launcherKeyDownCallback);
      };
    }, [isOpen, anchorRef]);

    const setInitialFocus = () => {
      let derivedInitialFocus = initialFocus;
      if (initialFocus === 'auto') {
        // Modeless mode - do not steal focus by default
        derivedInitialFocus = 'none';
      }

      // Case: 'popup'
      // Focus on Popup container
      if (derivedInitialFocus === 'popup') {
        focusOn(contentWrapperRef.current as HTMLElement);
      }

      // Case: 'firstFocusable'
      // Focus on first tabbable in the Popup container or container itself if there is none
      if (derivedInitialFocus === 'firstFocusable') {
        focusWithin(contentWrapperRef.current as HTMLElement);
      }
    };

    const getMainAxisOffset = (offset?: Offset) => {
      let calcOffset = 0;
      if (offset) {
        // @ts-ignore as OffsetFunction is going to be removed
        // Property 'mainAxis' does not exist on type 'OffsetFunction'.
        calcOffset = typeof offset === 'number' ? offset : offset.mainAxis || 0;
        if (tail === 'simple') {
          calcOffset += 5;
        }
      }
      return calcOffset;
    };

    const getCrossAxisOffset = (offset?: Offset) => {
      if (!offset) {
        return 0;
      } else {
        // @ts-ignore as OffsetFunction is going to be removed
        // Property 'crossAxis' does not exist on type 'OffsetFunction'.
        return typeof offset === 'number' ? 0 : offset.crossAxis || 0;
      }
    };

    const finalOffset = {
      mainAxis: getMainAxisOffset(offset),
      crossAxis: getCrossAxisOffset(offset)
    };

    // Preact FocusEvent mapping
    // onFocus - focusin
    // onBlur - focusout
    //
    // https://developer.mozilla.org/en-US/docs/Web/API/FocusEvent/relatedTarget
    // Event name   target                           relatedTarget
    // blur         The EventTarget losing focus     The EventTarget receiving focus (if any).
    // focus        The EventTarget receiving focus  The EventTarget losing focus (if any)
    // focusin      The EventTarget receiving focus  The EventTarget losing focus (if any)
    // focusout     The EventTarget losing focus     The EventTarget receiving focus (if any)
    const handleOnFocus = (event: FocusEvent) => {
      // Executing this listener means that element receiving focus lives within Popup.
      // We are checking which element is loosing focus to avoid activating FocusTrap when focus
      // travels among elements within FocusTrap.
      // To enable FocusTrap the element that looses focus must live out of the Popup or is Popup's container.
      if (
        !contentWrapperRef.current?.contains(event.relatedTarget as Element) ||
        event.relatedTarget === contentWrapperRef.current
      ) {
        // Do not activate focusTrap if the event.target is Popup itself - if we clicked on Popup's container
        // We should be able to focus Popup container without activating FocusTrap
        if (event.target != contentWrapperRef.current) {
          // Enable FocusTrap.
          setTrapDisabled(false);
        }
      }
    };
    const handleOnBlur = (event: FocusEvent) => {
      // Executing this listener means that an element living within Popup is loosing focus.
      // We are checking which element is receiving focus to avoid disabling FocusTrap when focs
      // travels among elements within FocusTrap.
      // To disable FocusTrap element the element that receives focus must live out of the Popup or is Popup's container.
      if (
        !contentWrapperRef.current?.contains(event.relatedTarget as Element) ||
        event.relatedTarget === contentWrapperRef.current
      ) {
        // Disable FocusTrap.
        setTrapDisabled(true);
      }
    };

    const handlePopupKeyDown = (event: KeyboardEvent) => {
      const currentlyFocusedEl = getActiveElement();
      const wrapperEl = contentWrapperRef.current;
      const launcherEl = launcherRef.current;
      if (wrapperEl === currentlyFocusedEl || wrapperEl?.contains(currentlyFocusedEl)) {
        switch (event.code) {
          case 'Escape': {
            onClose?.(event);
            break;
          }
          case 'F6':
            // Prevent default F6 handlers.
            // F6 is a standard Chrome address bar shortcut on Windows.
            event.preventDefault();
            event.stopPropagation();

            // Focus launcher
            if (launcherEl) {
              focusOn(launcherEl);
            }
            // Disable FocusTrap
            setTrapDisabled(true);
            break;
          case 'Tab':
            // If tabbing out of the popup when the popup itself has focus and the focus trap is
            // disabled, transfer focus back to the launcher.  Don't let the browser then
            // transfer focus itself from there like it normally would.
            if (event.target === contentWrapperRef.current && trapDisabled) {
              // Focus launcher
              if (launcherEl) {
                focusOn(launcherEl);

                // Prevent the browser from transferring focus itself.
                event.preventDefault();
              }
            }
            break;
        }
      }
    };

    const onPosition = useCallback(
      (data: PositionData) => {
        if (placement !== data.placement) {
          //if flips happens we need to change the placement in the animations.
          setAnimationPlacement(data.placement);
        }
      },
      [setAnimationPlacement, placement]
    );

    useLayoutEffect(() => {
      if (
        status === 'unmounted' ||
        contentWrapperRef.current == null ||
        popupBgColor != null ||
        tail === 'none'
      )
        return;

      const backgroundColorVar = getVarName(popupVars.backgroundColor);
      const computedStyle = getComputedStyle(contentWrapperRef.current!);
      setPopupBgColor(computedStyle.getPropertyValue(backgroundColorVar));
    }, [status, contentWrapperRef, popupBgColor, tail]);

    // Styles
    const { baseTheme, classes } = useComponentTheme(PopupRedwoodTheme);
    const isMounted = status !== 'unmounted';

    const renderPopup = () => {
      return (
        <Floating
          ref={stableRef}
          backgroundColor={popupBgColor}
          anchorRef={anchorRef}
          placement={placement}
          offsetValue={finalOffset}
          flipOptions={flipOptions}
          shiftOptions={shiftOptions}
          onClickOutside={onClickOutside}
          tail={tail}
          onPosition={onPosition}>
          <div
            ref={contentWrapperRef}
            tabIndex={-1}
            role={role}
            className={classNames([baseTheme, classes])}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            onKeyDown={handlePopupKeyDown}
            {...testIdProps}>
            {/*Setting restoreFocusRef to false because otherwise FocusTrap is stealing the focus back when :*/}
            {/*we click outside of modeless Popup (that keeps being open).*/}
            <FocusTrap isDisabled={trapDisabled} restoreFocusRef={false}>
              {children}
            </FocusTrap>
          </div>
        </Floating>
      );
    };

    if (isMounted) {
      if (modality === 'modal') {
        return <Modal isOpen={isMounted}>{renderPopup()}</Modal>;
      } else {
        return <Layer>{renderPopup()}</Layer>;
      }
    } else return null;
  }
);
